import React from 'react';
import Typography from '@mui/material/Typography';
import { parsePressure } from '../../../utilities/bloodPressureUtils';
import { parsePercentage } from '../../../utilities/hbA1cUtils';
import TestResultModalProps from '.';
import { ModalStates } from '../../../hooks/useModal';
import BiologicalResults from './BiologicalResults/BiologicalResults';
import QRISK from './QRISK/QRISK';
import PersonalData from './PersonalData/PersonalData';
import AnsweredQuestions from './AnsweredQuestions/AnsweredQuestions';
import TestImages from './TestImages/TestImages';
import ResultData from './ResultData/ResultData';
import TestMetadata from './TestMetadata/TestMetadata';
import { formatDateToLongFormat } from '../../../utilities/dateTimeUtils';
import { formatStatus, getStatusColor } from '../../../utilities/utils';
import Colors from '../../../theme/colors';
import { Icon } from '../../../components/Icon';
import {
  TestResultDialog,
  ContentContainer,
  TitleContainer,
  Title,
  TestDetailsContainer,
  TestDetailsRowContainer,
  TestDetailsItemLarge,
  TestDetailsItem,
  TestStatusContainer,
  StatusIndicator,
  TestId,
  Date,
  PdfLabel,
  PdfLink,
  PdfPass,
  PersonalDataContainer,
  EHRIntegrationsContainer,
  FeedbackAnswersContainer,
  QriskContainer,
  AnsweredQuestionsContainer,
  TestImagesContainer,
  TestMetadataContainer,
  ResultDataContainer,
  CloseButtonContainer,
  WarningContainer,
  WarningMessage,
  CloseButton,
  InfoFieldLabel,
  ErrorLogsContainer,
} from './styles';
import FeedbackAnswers from './FeedbackAnswers/FeedbackAnswers';
import ErrorLogs from './ErrorLogs/ErrorLogs';
import EHRIntegrations from './EHRIntegrations/EHRIntegrations';
import Measurements from './Measurements/Measuremets';

function TestResultModal({ modalState, onClose, selectedResult }: TestResultModalProps) {
  if (selectedResult) {
    const isOpen = modalState !== ModalStates.CLOSED;
    const {
      results: {
        status,
        modelId: chipModelId,
        warningMessage,
        totalCholesterol,
        totalHdlRatio,
        hdl,
        nonHdl,
        ldl,
        triglycerides,
        images,
        resultData,
        metadata,
        qrisk,
        pdfName,
        pdfPass,
        pdfUrl,
      },
      subjectData,
      errorLogs,
      nhsEmailMeta,
      pkbMessageMeta,
      gpMessageMeta,
      testType,
    } = selectedResult;
    const hasResults = status === 'successful' || status === 'warning';

    const biologicalSex = subjectData?.personalAnswers?.find(
      (answer) => answer.questionTitle === 'sex'
    )?.value;

    const hadCardiovascularDisease = subjectData?.answers?.find((answer) =>
      answer.label.includes('cardiovascular disease')
    )?.value;

    const bloodPressureString = subjectData?.answers.find((answer) =>
      answer.label.includes('blood pressure')
    )?.value;

    const pressureValues = parsePressure(bloodPressureString as string);

    const hba1cString = subjectData?.answers.find((answer) =>
      answer.label.includes('HbA1c')
    )?.value;

    const hba1cValue = parsePercentage(hba1cString as string);

    const diabetesStatusString = subjectData?.answers.find((answer) =>
      answer.label.includes('diabetes')
    )?.value;

    const isSecondaryPrevention = diabetesStatusString !== 'None';

    const formatNotificationFlag = (flag?: boolean | null) => {
      if (flag === null || flag === undefined) return '-';
      return flag ? 'Yes' : 'No';
    };

    const metadataAnswers = [
      ...metadata,
      {
        label: 'Notifications Enabled at Capture',
        value: formatNotificationFlag(subjectData.pushNotificationsEnabledAtImaging),
      },
    ];

    return (
      <TestResultDialog onClose={onClose} open={isOpen} maxWidth="md" fullWidth>
        <ContentContainer>
          <TitleContainer>
            <Title variant="Heading3" color={Colors.Text.Heading}>
              Test Results
            </Title>
          </TitleContainer>
          {warningMessage ? (
            <WarningContainer>
              <Icon name="warning" color={getStatusColor(status)} />
              <WarningMessage variant="BodySmallRegular" color={Colors.Text.Paragraph}>
                {warningMessage}
              </WarningMessage>
            </WarningContainer>
          ) : null}
          <TestDetailsContainer>
            <TestDetailsRowContainer>
              <TestDetailsItemLarge>
                <InfoFieldLabel> Test ID </InfoFieldLabel>
                <TestId>{subjectData.testId}</TestId>
              </TestDetailsItemLarge>
              <TestDetailsItem>
                <InfoFieldLabel> Test Performed </InfoFieldLabel>
                <Date>{formatDateToLongFormat(subjectData.testStartedAt)}</Date>
              </TestDetailsItem>
            </TestDetailsRowContainer>
            <TestDetailsRowContainer>
              <TestDetailsItem>
                <PdfLabel> PDF File </PdfLabel>
                {pdfUrl ? (
                  <PdfLink href={`${pdfUrl}`} target="_blank">{`${pdfName || 'N/A'}`}</PdfLink>
                ) : (
                  <PdfPass>N/A</PdfPass>
                )}
              </TestDetailsItem>
              <TestDetailsItem>
                <InfoFieldLabel> Test Status </InfoFieldLabel>
                <TestStatusContainer>
                  <StatusIndicator sx={{ backgroundColor: getStatusColor(status) }} />
                  <Typography variant="BodySmallRegular" color={Colors.Text.Paragraph}>
                    {formatStatus(status)}
                  </Typography>
                </TestStatusContainer>
              </TestDetailsItem>
            </TestDetailsRowContainer>
            <TestDetailsRowContainer>
              <TestDetailsItem>
                <PdfLabel> PDF Password </PdfLabel>
                <PdfPass>{`${pdfPass || 'N/A'}`}</PdfPass>
              </TestDetailsItem>
            </TestDetailsRowContainer>
          </TestDetailsContainer>
          {hasResults ? (
            <BiologicalResults
              totalCholesterol={totalCholesterol}
              hdl={hdl}
              nonHdl={nonHdl}
              ldl={ldl}
              totalHdlRatio={totalHdlRatio}
              triglycerides={triglycerides}
              isFemale={biologicalSex === 'Female'}
              hadCardiovascularDisease={hadCardiovascularDisease === 'Yes'}
              pipelineModelRanges={subjectData?.pipelineModelRanges}
              modelId={chipModelId ?? subjectData?.modelId}
            />
          ) : (
            <BiologicalResults
              isFemale={biologicalSex === 'Female'}
              hadCardiovascularDisease={hadCardiovascularDisease === 'Yes'}
              pipelineModelRanges={subjectData?.pipelineModelRanges}
              modelId={chipModelId ?? subjectData?.modelId}
            />
          )}
          <Measurements
            bmiValue={subjectData.bmi}
            ethnicity={subjectData.ethnicity}
            hbA1c={{ hbA1cValue: hba1cValue, isSecondaryPrevention }}
            pressureValues={{
              systolic: pressureValues.systolic,
              diastolic: pressureValues.diastolic,
            }}
            testType={testType}
          />
          <QriskContainer>
            <QRISK
              heartAge={qrisk.healthyHeartAge}
              scoreHealthy={qrisk.scoreHealthyPerson}
              score10Year={qrisk.score10Year}
              status={qrisk.status}
              resultStatus={status}
            />
          </QriskContainer>

          <PersonalDataContainer>
            <PersonalData
              personalAnswers={subjectData.personalAnswers}
              nhsNumber={subjectData.nhsNumber}
              odsCode={subjectData.odsCode}
            />
          </PersonalDataContainer>

          <EHRIntegrationsContainer>
            <EHRIntegrations
              nhsEmailMeta={nhsEmailMeta}
              pkbMessageMeta={pkbMessageMeta}
              gpMessageMeta={gpMessageMeta}
            />
          </EHRIntegrationsContainer>

          <FeedbackAnswersContainer>
            <FeedbackAnswers feedbackAnswers={subjectData.feedbackAnswers} />
          </FeedbackAnswersContainer>

          <AnsweredQuestionsContainer>
            <AnsweredQuestions answers={subjectData.answers} />
          </AnsweredQuestionsContainer>

          <TestImagesContainer>
            <TestImages images={images} />
          </TestImagesContainer>
          <TestMetadataContainer>
            <TestMetadata metadataAnswers={metadataAnswers} />
          </TestMetadataContainer>
          <ResultDataContainer>
            <ResultData
              showResultData={hasResults}
              resultData={[
                { value: 'L', ...resultData.l },
                { value: 'a', ...resultData.a },
                { value: 'b', ...resultData.b },
                {
                  value: 'Raw Values',
                  ...resultData.rawValues,
                },
                {
                  value: 'Published Values',
                  ...resultData.publishedValues,
                },
              ]}
            />
          </ResultDataContainer>
          <ErrorLogsContainer>
            <ErrorLogs errorLogs={errorLogs} />
          </ErrorLogsContainer>
          <CloseButtonContainer>
            <CloseButton variant="outlined" onClick={onClose}>
              Close
            </CloseButton>
          </CloseButtonContainer>
        </ContentContainer>
      </TestResultDialog>
    );
  }
  return null;
}

export default TestResultModal;
